import { useState } from 'react';
import { AlertTriangle, Download, Trash2 } from 'lucide-react';
import { useAnalysisDataStore } from '../stores/analysisDataStore';
import { useAnalysisStore } from '../stores/analysisStore';
import { format } from 'date-fns';
import { cn } from '../utils/cn';

const MAX_RESULTS = 20;

const DataLimitWarning = () => {
  const { results, isOverLimit, deleteResult, getOldestResults } = useAnalysisDataStore();
  const { addResult: addLocalResult } = useAnalysisStore();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!isOverLimit) {
    return null;
  }

  const handleSaveAndDelete = async () => {
    try {
      setIsDeleting(true);
      
      // Get the oldest results
      const oldestResults = await getOldestResults(5);
      
      if (oldestResults.length === 0) {
        return;
      }
      
      // Save to local storage first
      for (const result of oldestResults) {
        const data = { ...result.data };
        // Convert predicted100m to number if it exists
        if (data.predicted100m !== undefined) {
          data.predicted100m = typeof data.predicted100m === 'string' 
            ? Number(data.predicted100m) 
            : data.predicted100m;
        }
        
        addLocalResult({
          measurement_date: result.measurement_date,
          name: result.name,
          distance: result.distance,
          data,
          comment: result.comment
        });
      }
      
      // Then delete from the server
      for (const result of oldestResults) {
        await deleteResult(result.id);
      }
      
      setShowConfirm(false);
      alert(`最も古い${oldestResults.length}件のデータをローカルに保存し、サーバーから削除しました。`);
      
    } catch (err) {
      console.error('Error during save and delete operation:', err);
      alert('データの処理中にエラーが発生しました。');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportCSV = () => {
    const headers = [
      '日付',
      '名前',
      'スピード (m/s)',
      'ピッチ (歩/秒)',
      'ストライド (m)',
      '接地時間 (秒)',
      '滞空時間 (秒)',
      '100m予測 (秒)',
      'コメント'
    ].join(',');

    const rows = results.map(result => [
      format(new Date(result.measurement_date), 'yyyy/MM/dd'),
      result.name,
      result.data.speed?.toFixed(2) || '',
      result.data.pitch?.toFixed(2) || '',
      result.data.stride?.toFixed(2) || '',
      result.data.contactTime?.toFixed(3) || '',
      result.data.flightTime?.toFixed(3) || '',
      (typeof result.data.predicted100m === 'string' ? parseFloat(result.data.predicted100m).toFixed(2) : typeof result.data.predicted100m === 'number' ? result.data.predicted100m.toFixed(2) : ''),
      `"${result.comment || ''}"`
    ].join(','));

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sprint-analysis-${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="fixed bottom-4 right-4 max-w-sm p-4 rounded-lg shadow-lg bg-red-50 z-50">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <AlertTriangle className="h-6 w-6 text-red-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-red-800">
            データ保存数が上限に達しています
          </h3>
          <p className="mt-2 text-sm text-red-700">
            サーバーに保存できるデータは最大{MAX_RESULTS}件までです。新しいデータを保存するには、古いデータを削除する必要があります。
          </p>
          
          {!showConfirm ? (
            <div className="mt-3 flex flex-col space-y-2">
              <button
                onClick={() => setShowConfirm(true)}
                className="btn btn-sm bg-red-600 text-white hover:bg-red-700 flex items-center justify-center"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                古いデータを削除
              </button>
              <button
                onClick={handleExportCSV}
                className="btn btn-sm bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-1" />
                全データをCSVで保存
              </button>
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              <p className="text-sm font-medium text-red-800">
                最も古い5件のデータをローカルに保存し、サーバーから削除しますか？
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveAndDelete}
                  disabled={isDeleting}
                  className={cn(
                    "btn btn-sm flex-1 bg-red-600 text-white hover:bg-red-700",
                    isDeleting && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {isDeleting ? '処理中...' : '保存して削除'}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={isDeleting}
                  className="btn btn-sm flex-1 bg-gray-600 text-white hover:bg-gray-700"
                >
                  キャンセル
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataLimitWarning;